import { useEffect, useMemo, useState } from "react";
import { Ban, Bot, Loader2, RefreshCw, ShieldOff, UserRound } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import {
  blockAuditVisitor,
  ChatbotAuditMessage,
  ChatbotAuditSession,
  getAuditSessionMessages,
  listAuditSessions,
  unblockAuditVisitor,
} from "./api/chatbotApi";

type AuditState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; sessions: ChatbotAuditSession[] };

const formatDate = (value: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ja-JP", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isBotRole = (role: string) => role === "bot" || role === "assistant";

export const ChatbotAuditTab = () => {
  const [state, setState] = useState<AuditState>({ kind: "loading" });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatbotAuditMessage[]>([]);
  const [selectedSession, setSelectedSession] = useState<ChatbotAuditSession | null>(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [busyIpHash, setBusyIpHash] = useState<string | null>(null);

  const loadSessions = async (keepSelection = true) => {
    setState((prev) => (prev.kind === "ready" ? prev : { kind: "loading" }));
    try {
      const res = await listAuditSessions(50);
      setState({ kind: "ready", sessions: res.items });
      if (!keepSelection || !selectedId) {
        setSelectedId(res.items[0]?.id ?? null);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "会話監査の読み込みに失敗しました。";
      setState({ kind: "error", message });
    }
  };

  useEffect(() => {
    void loadSessions(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      setSelectedSession(null);
      return;
    }
    let cancelled = false;
    setLoadingMessages(true);
    (async () => {
      try {
        const res = await getAuditSessionMessages(selectedId);
        if (cancelled) return;
        setSelectedSession(res.session);
        setMessages(res.messages);
      } catch (err) {
        if (cancelled) return;
        window.alert(err instanceof Error ? err.message : "メッセージの読み込みに失敗しました。");
      } finally {
        if (!cancelled) setLoadingMessages(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const sessions = state.kind === "ready" ? state.sessions : [];
  const selectedFromList = useMemo(
    () => sessions.find((item) => item.id === selectedId) ?? null,
    [selectedId, sessions],
  );
  const activeSession = selectedSession ?? selectedFromList;

  const handleBlockToggle = async (session: ChatbotAuditSession) => {
    if (!session.ip_hash) return;
    setBusyIpHash(session.ip_hash);
    try {
      if (session.blocked) {
        await unblockAuditVisitor(session.ip_hash);
      } else {
        const reason = window.prompt("ブロック理由を入力してください", "不適切な質問 / token 消費対策");
        await blockAuditVisitor(session.ip_hash, { reason: reason?.trim() || undefined });
      }
      await loadSessions(true);
      if (selectedId) {
        const res = await getAuditSessionMessages(selectedId);
        setSelectedSession(res.session);
        setMessages(res.messages);
      }
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "ブロック設定の更新に失敗しました。");
    } finally {
      setBusyIpHash(null);
    }
  };

  if (state.kind === "loading") {
    return (
      <Card title="会話監査">
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#64748b" }}>
          <Loader2 size={16} className="spin" />
          読み込み中...
        </div>
      </Card>
    );
  }

  if (state.kind === "error") {
    return (
      <Card title="会話監査を読み込めません">
        <p style={{ color: "#dc2626", fontSize: 13, marginBottom: 12 }}>{state.message}</p>
        <Button onClick={() => void loadSessions(false)}>再読み込み</Button>
      </Card>
    );
  }

  return (
    <div className="grid-12" style={{ gap: 18 }}>
      <div className="col-5">
        <Card
          title="会話セッション"
          subtitle="訪問者ごとの質問と回答を確認します"
          actions={
            <Button
              variant="secondary"
              onClick={() => void loadSessions(true)}
              iconRight={<RefreshCw size={14} />}
            >
              更新
            </Button>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {sessions.length === 0 ? (
              <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>まだ会話がありません。</p>
            ) : (
              sessions.map((session) => (
                <button
                  key={session.id}
                  type="button"
                  onClick={() => setSelectedId(session.id)}
                  style={{
                    textAlign: "left",
                    border: selectedId === session.id ? "1px solid #60a5fa" : "1px solid rgba(148,163,184,0.28)",
                    background: selectedId === session.id ? "rgba(59,130,246,0.10)" : "rgba(255,255,255,0.76)",
                    borderRadius: 14,
                    padding: 12,
                    cursor: "pointer",
                    boxShadow: selectedId === session.id ? "0 14px 30px rgba(37,99,235,0.14)" : "none",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <strong style={{ fontSize: 13, color: "#0f172a" }}>{session.ip_hash_short}</strong>
                    <span
                      style={{
                        fontSize: 11,
                        color: session.blocked ? "#b91c1c" : "#15803d",
                        background: session.blocked ? "#fee2e2" : "#dcfce7",
                        borderRadius: 999,
                        padding: "2px 8px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {session.blocked ? "ブロック中" : "許可中"}
                    </span>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
                    {formatDate(session.last_message_at ?? session.created_at)} / {session.user_message_count} 問
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 12,
                      color: "#475569",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {session.page_url || "page_url なし"}
                  </div>
                </button>
              ))
            )}
          </div>
        </Card>
      </div>

      <div className="col-7">
        <Card
          title="会話内容"
          subtitle={activeSession ? `${activeSession.ip_hash_short} / ${formatDate(activeSession.created_at)}` : "セッションを選択してください"}
          actions={
            activeSession ? (
              <Button
                variant={activeSession.blocked ? "secondary" : "danger"}
                disabled={!activeSession.ip_hash || busyIpHash === activeSession.ip_hash}
                onClick={() => void handleBlockToggle(activeSession)}
                iconRight={activeSession.blocked ? <ShieldOff size={14} /> : <Ban size={14} />}
              >
                {busyIpHash === activeSession.ip_hash
                  ? "更新中..."
                  : activeSession.blocked
                    ? "解除"
                    : "ブロック"}
              </Button>
            ) : null
          }
        >
          {!activeSession ? (
            <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>左の一覧からセッションを選択してください。</p>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gap: 8,
                  marginBottom: 14,
                  padding: 12,
                  borderRadius: 14,
                  background: "rgba(241,245,249,0.72)",
                  color: "#475569",
                  fontSize: 12,
                }}
              >
                <div>ページ: {activeSession.page_url || "-"}</div>
                <div>セッション: {activeSession.id}</div>
                <div>メッセージ: user {activeSession.user_message_count} / bot {activeSession.bot_message_count}</div>
              </div>
              {loadingMessages ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#64748b" }}>
                  <Loader2 size={16} className="spin" />
                  メッセージ読み込み中...
                </div>
              ) : messages.length === 0 ? (
                <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>まだメッセージがありません。</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {messages.map((message) => {
                    const bot = isBotRole(message.role);
                    return (
                      <div
                        key={message.id}
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "flex-start",
                          justifyContent: bot ? "flex-start" : "flex-end",
                        }}
                      >
                        {bot && <Bot size={18} color="#2563eb" style={{ marginTop: 9 }} />}
                        <div
                          style={{
                            maxWidth: "82%",
                            borderRadius: 16,
                            padding: "10px 12px",
                            background: bot ? "rgba(248,250,252,0.92)" : "linear-gradient(135deg,#3678f4,#35c6d0)",
                            color: bot ? "#0f172a" : "#ffffff",
                            border: bot ? "1px solid rgba(148,163,184,0.28)" : "none",
                            whiteSpace: "pre-wrap",
                            lineHeight: 1.65,
                            fontSize: 13,
                          }}
                        >
                          {message.content}
                          <div style={{ marginTop: 6, fontSize: 11, opacity: 0.68 }}>
                            {message.role} / {formatDate(message.created_at)}
                          </div>
                        </div>
                        {!bot && <UserRound size={18} color="#0f766e" style={{ marginTop: 9 }} />}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
